import { useState, useEffect, useMemo } from "react";
import { useReportFilters } from "@/hooks/use-report-filters";
import { useReportExport } from "@/hooks/use-report-export";
import { useFleetReportData } from "@/hooks/use-fleet-report-data";
import { useReportAudit } from "@/hooks/use-report-audit";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useHasGpsTracking } from "@/hooks/useHasGpsTracking";
import { useAllVehiclesDetails, fetchVehicleDetail } from "@/hooks/use-all-vehicles-details";
import { usePdfFromHtml } from "@/hooks/usePdfFromHtml";
import { useQuery } from "@tanstack/react-query";
import { ReportContent } from "./components/ReportContent";
import ConsolidatedReportWrapper from "./components/ConsolidatedReportWrapper";
import SpecializedReportWrapper from "./components/SpecializedReportWrapper";
import CostCenterReportWrapper from "./components/CostCenterReportWrapper";
import { UniversalFiltersSheet, UniversalReportFilters } from "@/components/fleet/reports/UniversalFiltersSheet";
import { supabase } from "@/integrations/supabase/client";
import { REPORT_CONFIGS, getReportConfig } from "./utils/report-config";
import { mapRpcResultToFleetReportData, FleetReportData, EMPTY_REPORT_DATA } from "@/types/fleet-reports";
import { VehicleSummary } from "@/types/fleet-reports.v2";
import { 
  generateConsolidatedPDF_Pro, 
  buildConsolidatedPdfInput,
  PdfVehicleSummary,
  VehicleDetailData
} from "@/hooks/use-report-export.consolidated.pro";
import { generateVehiclesPDF_Pro, buildVehiclesPdfInput } from "@/hooks/use-report-export.vehicles.pro";
import { generateMaintenancePDF_Pro, buildMaintenancePdfInput } from "@/hooks/use-report-export.maintenance.pro";
// Tipo local para WorkOrder (usado apenas neste arquivo)
type WorkOrderWithItems = {
  id: string;
  order_number: number;
  description: string;
  status: string;
  total_cost: number | null;
  opened_at: string;
  closed_at: string | null;
  provider_id: string | null;
  vehicle_id: string;
  fleet_providers: { id: string; name: string } | null;
  fleet_vehicles: { id: string; plate: string; make: string | null; model: string | null; odometer: number | null; status: string } | null;
  fleet_work_order_items: Array<{ id: string; description: string; quantity: number; unit_price: number; total_price: number }>;
};
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { FileText, Download, Printer, SlidersHorizontal, CalendarIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

const DEFAULT_FILTERS: UniversalReportFilters = {
  reportType: '',
  dateRange: undefined,
  vehicleIds: [],
  driverIds: [],
  costCenterIds: [],
  groupBy: 'month',
  topN: 50,
  consolidatedPdfMode: 'top-n',
  consolidatedPdfTopN: 10,
  consolidatedPdfMaxVehicles: 80
};

export default function ReportsIndex() {
  const [filters, setFilters] = useState<UniversalReportFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<UniversalReportFilters>(DEFAULT_FILTERS);
  const [reportData, setReportData] = useState<FleetReportData | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { activeCompany } = useAuth();
  const { toast } = useToast();
  const { generatePdfBlob, isExporting } = useReportExport();
  const { fetchReportData, requiresPeriod, validateFilters } = useFleetReportData();
  const { startReportRun } = useReportAudit();
  const { hasGps } = useHasGpsTracking(activeCompany?.id);
  const { generatePdfFromElement } = usePdfFromHtml();

  // Mapeamento de veículos para o wrapper v2
  const vehiclesSummary = useMemo<VehicleSummary[]>(() => {
    if (!reportData?.byVehicle?.rows) return [];
    return reportData.byVehicle.rows.map(v => ({
      vehicle_id: v.vehicle_id,
      vehicle_label: v.vehicle_label,
      status: null,
      alertsCount: Number(v.kpis?.alertCount || 0),
      summary: {
        totalCost: Number(v.kpis?.totalCost || 0),
        costPerKm: Number(v.kpis?.costPerKm || 0),
        km: Number(v.kpis?.totalKm || 0),
        breakdown: {
          fuel: Number(v.kpis?.fuelCost || 0),
          maintenance: Number(v.kpis?.maintenanceCost || 0),
          tires: Number(v.kpis?.tireCost || 0),
          expenses: Number(v.kpis?.expensesCost || 0),
          documents: Number(v.kpis?.documentsCost || 0),
          trips: 0,
        }
      }
    }));
  }, [reportData?.byVehicle?.rows]);

  // Hook de detalhamento de TODOS os veículos
  const period = useMemo(() => {
    if (!appliedFilters.dateRange?.from || !appliedFilters.dateRange?.to) return undefined;
    return { from: appliedFilters.dateRange.from, to: appliedFilters.dateRange.to };
  }, [appliedFilters.dateRange]);

  const { allVehiclesDetails, isLoadingDetails } = useAllVehiclesDetails(vehiclesSummary, period);

  // Buscar centros de custo para o relatório de Centro de Custo
  const { data: costCentersData = [] } = useQuery({
    queryKey: ['cost-centers-report', activeCompany?.id, appliedFilters.costCenterIds],
    queryFn: async () => {
      if (!activeCompany?.id || appliedFilters.costCenterIds.length === 0) return [];
      const { data } = await supabase
        .from('cost_centers')
        .select('id, name, code')
        .eq('company_id', activeCompany.id)
        .in('id', appliedFilters.costCenterIds);
      return data || [];
    },
    enabled: !!activeCompany?.id && appliedFilters.costCenterIds.length > 0
  });

  // Buscar veículos com cost_center_id para agrupamento
  const { data: vehiclesWithCostCenter = [] } = useQuery({
    queryKey: ['vehicles-cost-center', activeCompany?.id, appliedFilters.costCenterIds],
    queryFn: async () => {
      if (!activeCompany?.id || appliedFilters.costCenterIds.length === 0) return [];
      const { data } = await supabase
        .from('fleet_vehicles')
        .select('id, cost_center_id')
        .eq('company_id', activeCompany.id)
        .in('cost_center_id', appliedFilters.costCenterIds);
      return data || [];
    },
    enabled: !!activeCompany?.id && appliedFilters.costCenterIds.length > 0
  });

  // Agrupar veículos por centro de custo
  const costCenterGroups = useMemo(() => {
    if (appliedFilters.reportType !== 'cost-centers' || !reportData?.byVehicle?.rows) return [];
    
    return costCentersData.map(cc => {
      const vehicleIdsInCC = vehiclesWithCostCenter
        .filter(v => v.cost_center_id === cc.id)
        .map(v => v.id);
      
      const vehiclesInCC = vehiclesSummary.filter(v => vehicleIdsInCC.includes(v.vehicle_id));
      
      const totals = vehiclesInCC.reduce((acc, v) => ({
        totalCost: acc.totalCost + (v.summary?.totalCost || 0),
        fuelCost: acc.fuelCost + (v.summary?.breakdown?.fuel || 0),
        maintenanceCost: acc.maintenanceCost + (v.summary?.breakdown?.maintenance || 0),
        tiresCost: acc.tiresCost + (v.summary?.breakdown?.tires || 0),
        expensesCost: acc.expensesCost + (v.summary?.breakdown?.expenses || 0),
        documentsCost: acc.documentsCost + (v.summary?.breakdown?.documents || 0),
        tripsCost: acc.tripsCost + (v.summary?.breakdown?.trips || 0),
        totalKm: acc.totalKm + (v.summary?.km || 0)
      }), { totalCost: 0, fuelCost: 0, maintenanceCost: 0, tiresCost: 0, expensesCost: 0, documentsCost: 0, tripsCost: 0, totalKm: 0 });
      
      return { costCenter: cc, vehicles: vehiclesInCC, totals };
    }).filter(g => g.vehicles.length > 0);
  }, [appliedFilters.reportType, costCentersData, vehiclesWithCostCenter, vehiclesSummary, reportData]);
  
  // Relatórios disponíveis (apenas os 5 principais)
  const availableReports = REPORT_CONFIGS;

  const handleReportTypeChange = (type: string) => {
    setFilters(prev => ({ ...prev, reportType: type }));
    // Limpar PDF anterior ao mudar tipo
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
      setPdfBlob(null);
      setReportData(null);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
      setPdfBlob(null);
      setReportData(null);
    }
  };

  const handleGeneratePDF = async () => {
    const currentFilters = { ...filters };
    setAppliedFilters(currentFilters);
    
    if (!currentFilters.reportType) {
      toast({ title: "Erro", description: "Selecione um tipo de relatório primeiro", variant: "destructive" });
      return;
    }

    if (!activeCompany?.id) {
      toast({ title: "Erro", description: "Empresa não selecionada", variant: "destructive" });
      return;
    }

    if (currentFilters.reportType === 'cost-centers' && currentFilters.costCenterIds.length === 0) {
      toast({ title: "Filtros inválidos", description: "Selecione pelo menos um centro de custo", variant: "destructive" });
      return;
    }

    const validationResult = validateFilters(currentFilters.reportType, {
      dateRange: currentFilters.dateRange ? { from: currentFilters.dateRange.from!, to: currentFilters.dateRange.to! } : undefined,
      vehicleIds: currentFilters.vehicleIds,
      driverIds: currentFilters.driverIds
    });

    if (!validationResult.valid && validationResult.error) {
      toast({ title: "Filtros inválidos", description: validationResult.error, variant: "destructive" });
      return;
    }

    setIsGenerating(true);
    const auditRun = await startReportRun(activeCompany.id, currentFilters.reportType, currentFilters);

    try {
      // Buscar dados se necessário
      let currentReportData = reportData;
      if (!currentReportData) {
        const rpcReportTypes = ['consolidated', 'vehicles', 'maintenance', 'fuel', 'cost-centers'];
        if (rpcReportTypes.includes(currentFilters.reportType)) {
          let vehicleIdsToUse = currentFilters.vehicleIds;
          if (currentFilters.reportType === 'cost-centers' && currentFilters.costCenterIds.length > 0) {
            const { data: vehiclesByCostCenter } = await supabase
              .from('fleet_vehicles').select('id').eq('company_id', activeCompany.id).in('cost_center_id', currentFilters.costCenterIds);
            vehicleIdsToUse = vehiclesByCostCenter?.map(v => v.id) || [];
            if (vehicleIdsToUse.length === 0) {
              toast({ title: "Aviso", description: "Nenhum veículo encontrado nos centros de custo selecionados" });
              setIsGenerating(false);
              return;
            }
          }
          currentReportData = await fetchReportData({
            reportType: currentFilters.reportType === 'cost-centers' ? 'consolidated' : currentFilters.reportType,
            filters: {
              dateRange: currentFilters.dateRange ? { from: currentFilters.dateRange.from!, to: currentFilters.dateRange.to! } : undefined,
              vehicleIds: vehicleIdsToUse, driverIds: currentFilters.driverIds, topN: currentFilters.topN
            }
          });
          if (currentReportData.error) throw new Error(currentReportData.error);
          setReportData(currentReportData);
        }
      }

      // GERADOR PDF PROFISSIONAL para consolidated
      if (currentFilters.reportType === 'consolidated' && currentReportData && currentFilters.dateRange?.from && currentFilters.dateRange?.to) {
        const periodLabel = `${format(currentFilters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })} – ${format(currentFilters.dateRange.to, "dd/MM/yyyy", { locale: ptBR })}`;
        
        const pdfVehiclesSummary: PdfVehicleSummary[] = (currentReportData?.byVehicle?.rows ?? []).map((v: any) => ({
          vehicle_id: String(v.vehicle_id ?? v.id),
          vehicle_label: String(v.vehicle_label ?? v.label ?? "-"),
          km: Number(v.kpis?.totalKm ?? v.km ?? 0),
          cost: Number(v.kpis?.totalCost ?? v.cost ?? 0),
          costPerKm: Number(v.kpis?.costPerKm ?? v.costPerKm ?? 0),
          fleetPercent: Number(v.kpis?.fleetPercent ?? v.fleetPercent ?? 0),
          alerts: (Array.isArray(v.alerts) && v.alerts.length > 0) 
            ? `${v.alerts.length} alerta${v.alerts.length > 1 ? 's' : ''}` 
            : "OK",
          breakdown: {
            fuel: Number(v.kpis?.fuelCost ?? 0),
            maintenance: Number(v.kpis?.maintenanceCost ?? 0),
            tires: Number(v.kpis?.tireCost ?? 0),
            expenses: Number(v.kpis?.expensesCost ?? 0),
            documents: Number(v.kpis?.documentsCost ?? 0),
            trips: 0
          }
        }));

        // Período correto a partir de currentFilters (não appliedFilters)
        const pdfPeriod = { 
          from: currentFilters.dateRange!.from!, 
          to: currentFilters.dateRange!.to! 
        };

        const input = buildConsolidatedPdfInput({
          reportData: currentReportData,
          vehiclesSummary: pdfVehiclesSummary,
          periodLabel,
          companyName: activeCompany.name,
          allVehiclesDetails: allVehiclesDetails as Map<string, VehicleDetailData>,
          // Callback que busca dados do banco quando o cache está vazio
          fetchVehicleDetail: async (vehicleId: string) => {
            // Primeiro tenta o cache
            const cached = allVehiclesDetails.get(vehicleId);
            if (cached && (cached.fuel?.length > 0 || cached.maintenance?.length > 0)) {
              return cached;
            }
            
            // Fallback: buscar do banco com o período correto
            const fallbackSummary = pdfVehiclesSummary.find(v => v.vehicle_id === vehicleId);
            return fetchVehicleDetail(vehicleId, pdfPeriod, {
              vehicle_id: vehicleId,
              vehicle_label: fallbackSummary?.vehicle_label ?? "-",
              summary: {
                totalCost: fallbackSummary?.cost ?? 0,
                costPerKm: fallbackSummary?.costPerKm ?? 0,
                km: fallbackSummary?.km ?? 0,
                breakdown: {
                  fuel: fallbackSummary?.breakdown?.fuel ?? 0,
                  maintenance: fallbackSummary?.breakdown?.maintenance ?? 0,
                  tires: fallbackSummary?.breakdown?.tires ?? 0,
                  expenses: fallbackSummary?.breakdown?.expenses ?? 0,
                  documents: fallbackSummary?.breakdown?.documents ?? 0,
                  trips: fallbackSummary?.breakdown?.trips ?? 0
                }
              }
            });
          },
          options: {
            mode: currentFilters.consolidatedPdfMode ?? 'top-n',
            topN: currentFilters.consolidatedPdfTopN ?? 10,
            maxVehicles: currentFilters.consolidatedPdfMaxVehicles ?? 80
          }
        });

        const blob = await generateConsolidatedPDF_Pro(input);
        const filename = `FleetONE-Consolidado-${format(new Date(), "yyyy-MM-dd")}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        await auditRun.complete({ status: 'success', rowsProcessed: pdfVehiclesSummary.length });
        toast({ title: "Sucesso", description: "PDF profissional gerado com sucesso", duration: 2000 });
      } else if (currentFilters.reportType === 'vehicles' && currentReportData && currentFilters.dateRange?.from && currentFilters.dateRange?.to) {
        // PDF Profissional para Veículos - COMPLETO com detalhamento
        const periodLabel = `${format(currentFilters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })} – ${format(currentFilters.dateRange.to, "dd/MM/yyyy", { locale: ptBR })}`;
        
        const vehicleData = (currentReportData?.byVehicle?.rows ?? []).map((v: any) => ({
          vehicle_id: v.vehicle_id,
          vehicle_plate: v.vehicle_label,
          vehicle_model: v.model || "-",
          total_trips: v.kpis?.tripCount || 0,
          total_distance: v.kpis?.totalKm || 0,
          total_fuel_cost: v.kpis?.fuelCost || 0,
          total_maintenance_cost: v.kpis?.maintenanceCost || 0,
          fuel_efficiency: v.kpis?.efficiency || 0,
        }));

        // Construir vehiclesSummary para detalhamento
        const pdfVehiclesSummary = (currentReportData?.byVehicle?.rows ?? []).map((v: any) => ({
          vehicle_id: String(v.vehicle_id ?? v.id),
          vehicle_label: String(v.vehicle_label ?? v.label ?? "-"),
          model: v.model || "-",
          km: Number(v.kpis?.totalKm ?? v.km ?? 0),
          cost: Number(v.kpis?.totalCost ?? v.cost ?? 0),
          costPerKm: Number(v.kpis?.costPerKm ?? v.costPerKm ?? 0),
          efficiency: Number(v.kpis?.efficiency ?? 0),
          fuelCost: Number(v.kpis?.fuelCost ?? 0),
          maintenanceCost: Number(v.kpis?.maintenanceCost ?? 0),
          trips: Number(v.kpis?.tripCount ?? 0),
          breakdown: {
            fuel: Number(v.kpis?.fuelCost ?? 0),
            maintenance: Number(v.kpis?.maintenanceCost ?? 0),
            tires: Number(v.kpis?.tireCost ?? 0),
            expenses: Number(v.kpis?.expensesCost ?? 0),
            documents: Number(v.kpis?.documentsCost ?? 0),
            trips: 0
          }
        }));

        // Período correto para buscar detalhes
        const pdfPeriod = { 
          from: currentFilters.dateRange!.from!, 
          to: currentFilters.dateRange!.to! 
        };

        const input = buildVehiclesPdfInput({ 
          vehicleData, 
          periodLabel, 
          companyName: activeCompany.name,
          vehiclesSummary: pdfVehiclesSummary,
          allVehiclesDetails: allVehiclesDetails as Map<string, VehicleDetailData>,
          // Callback que busca dados do banco quando o cache está vazio
          fetchVehicleDetail: async (vehicleId: string) => {
            // Primeiro tenta o cache
            const cached = allVehiclesDetails.get(vehicleId);
            if (cached && (cached.fuel?.length > 0 || cached.maintenance?.length > 0)) {
              return cached as VehicleDetailData;
            }
            
            // Fallback: buscar do banco com o período correto
            const fallbackSummary = pdfVehiclesSummary.find(v => v.vehicle_id === vehicleId);
            return fetchVehicleDetail(vehicleId, pdfPeriod, {
              vehicle_id: vehicleId,
              vehicle_label: fallbackSummary?.vehicle_label ?? "-",
              summary: {
                totalCost: fallbackSummary?.cost ?? 0,
                costPerKm: fallbackSummary?.costPerKm ?? 0,
                km: fallbackSummary?.km ?? 0,
                breakdown: {
                  fuel: fallbackSummary?.breakdown?.fuel ?? 0,
                  maintenance: fallbackSummary?.breakdown?.maintenance ?? 0,
                  tires: fallbackSummary?.breakdown?.tires ?? 0,
                  expenses: fallbackSummary?.breakdown?.expenses ?? 0,
                  documents: fallbackSummary?.breakdown?.documents ?? 0,
                  trips: fallbackSummary?.breakdown?.trips ?? 0
                }
              }
            }) as Promise<VehicleDetailData>;
          },
          options: {
            mode: currentFilters.consolidatedPdfMode ?? 'top-n',
            topN: currentFilters.consolidatedPdfTopN ?? 10,
            maxVehicles: currentFilters.consolidatedPdfMaxVehicles ?? 80
          }
        });

        const blob = await generateVehiclesPDF_Pro(input);
        const filename = `FleetONE-Veiculos-${format(new Date(), "yyyy-MM-dd")}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        await auditRun.complete({ status: 'success', rowsProcessed: pdfVehiclesSummary.length });
        toast({ title: "Sucesso", description: "PDF de Veículos gerado com detalhamento completo", duration: 2000 });
      } else if (currentFilters.reportType === 'maintenance') {
        // PDF Profissional para Manutenção - buscar dados específicos
        const periodLabel = currentFilters.dateRange?.from && currentFilters.dateRange?.to
          ? `${format(currentFilters.dateRange.from, "dd/MM/yyyy", { locale: ptBR })} – ${format(currentFilters.dateRange.to, "dd/MM/yyyy", { locale: ptBR })}`
          : "Período não especificado";

        // Buscar dados de manutenção COM plan_id e dados do plano
        const { data: workOrders } = await supabase
          .from('fleet_work_orders')
          .select('*, fleet_vehicles(plate), fleet_providers(name), fleet_maint_plans(id, name, type, periodicity_km, periodicity_days)')
          .eq('company_id', activeCompany.id);

        // Buscar TODOS os planos (ativos + pendentes de aprovação)
        const { data: allPlans } = await supabase
          .from('fleet_maint_plans')
          .select('*, fleet_vehicles(plate, odometer)')
          .eq('company_id', activeCompany.id);

        // NÃO filtrar por status - incluir todos os planos que têm OSs
        const pendingApprovalPlansRaw = (allPlans || []).filter(p => p.approval_status === 'pending');

        const formattedOrders = (workOrders || []).map(o => ({
          ...o,
          vehicle_plate: o.fleet_vehicles?.plate,
          provider_name: o.fleet_providers?.name,
          plan_id: o.plan_id,
          plan_name: o.fleet_maint_plans?.name,
          plan_type: o.fleet_maint_plans?.type,
        }));

        // Formatar todos os planos (não apenas ativos, para vincular OSs)
        const formattedPlans = (allPlans || []).map(p => {
          const daysUntil = p.next_execution_date 
            ? Math.ceil((new Date(p.next_execution_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            : null;
          const kmUntil = p.next_execution_km && p.fleet_vehicles?.odometer
            ? p.next_execution_km - p.fleet_vehicles.odometer
            : null;
          return {
            ...p,
            vehicle_plate: p.fleet_vehicles?.plate,
            days_until_maintenance: daysUntil,
            km_until_maintenance: kmUntil,
          };
        });

        // Formatar planos pendentes de aprovação
        const pendingApprovalPlans = pendingApprovalPlansRaw.map(p => ({
          id: p.id,
          vehicleLabel: p.fleet_vehicles?.plate || "-",
          planName: p.name || "-",
          type: p.type === 'preventive' ? 'Preventiva' : p.type === 'corrective' ? 'Corretiva' : p.type || "-",
          periodicityLabel: p.periodicity_km 
            ? `${p.periodicity_km.toLocaleString('pt-BR')} km`
            : p.periodicity_days 
              ? `${p.periodicity_days} dias`
              : "-",
          createdAt: p.created_at,
          createdByName: undefined,
        }));

        // Montar resumo de veículos para detalhamento
        const vehicleMap = new Map<string, { label: string; count: number; cost: number }>();
        formattedOrders.forEach(o => {
          const id = o.vehicle_id;
          const label = o.vehicle_plate || "-";
          const existing = vehicleMap.get(id) || { label, count: 0, cost: 0 };
          existing.count++;
          existing.cost += (o.total_cost || 0);
          vehicleMap.set(id, existing);
        });

        const vehiclesSummary = Array.from(vehicleMap.entries())
          .map(([id, data]) => ({
            vehicle_id: id,
            vehicle_label: data.label,
            ordersCount: data.count,
            totalCost: data.cost,
          }))
          .sort((a, b) => b.totalCost - a.totalCost);

        // Função para buscar detalhes de manutenção por veículo - ESTRUTURA HIERÁRQUICA
        const fetchVehicleMaintenanceDetail = async (vehicleId: string) => {
          const vehicleOrders = formattedOrders.filter(o => o.vehicle_id === vehicleId);
          
          // NOVA LÓGICA: Trazer APENAS planos relevantes do veículo
          // Status válidos: active, pending, ou aguardando aprovação
          // Planos inativos (concluídos) NÃO devem aparecer no relatório
          const allVehiclePlans = formattedPlans.filter(p => 
            p.vehicle_id === vehicleId && (
              p.status === 'active' ||
              p.status === 'pending' ||
              p.approval_status === 'pending'
            )
          );
          
          // IDs dos planos relevantes para identificar OSs órfãs
          const relevantPlanIds = new Set(allVehiclePlans.map(p => p.id));
          
          const vehicle = vehiclesSummary.find(v => v.vehicle_id === vehicleId);
          const completedOrders = vehicleOrders.filter(o => o.status === 'closed').length;
          const openOrders = vehicleOrders.filter(o => o.status === 'open' || o.status === 'in_progress').length;
          const totalCost = vehicleOrders.reduce((sum, o) => sum + (o.total_cost || 0), 0);
          const lastOrder = [...vehicleOrders].sort((a, b) => (b.closed_at || b.opened_at || "").localeCompare(a.closed_at || a.opened_at || ""))[0];

          // Função helper para calcular status do plano
          // CORRIGIDO: Reconhecer status 'pending' e 'awaiting_approval'
          const getPlanStatus = (p: typeof formattedPlans[0]): 'ok' | 'upcoming' | 'critical' | 'overdue' | 'inactive' | 'pending' | 'awaiting_approval' => {
            // Plano concluído ou inativo - não deve aparecer no relatório
            if (p.status === 'completed' || p.status === 'inactive') return 'inactive';
            
            // Plano aguardando aprovação
            if (p.approval_status === 'pending') return 'awaiting_approval';
            
            // Plano pendente de execução (ainda não ativo)
            if (p.status === 'pending') return 'pending';
            
            // Plano ativo - calcular urgência baseado em dias/km
            const daysUntil = p.days_until_maintenance;
            const kmUntil = p.km_until_maintenance;
            if ((daysUntil !== null && daysUntil < 0) || (kmUntil !== null && kmUntil < 0)) return 'overdue';
            if ((daysUntil !== null && daysUntil <= 7) || (kmUntil !== null && kmUntil < 500)) return 'critical';
            if ((daysUntil !== null && daysUntil <= 30) || (kmUntil !== null && kmUntil < 2000)) return 'upcoming';
            return 'ok';
          };

          const getInterval = (p: typeof formattedPlans[0]) => 
            p.periodicity_km 
              ? `${p.periodicity_km.toLocaleString('pt-BR')} km`
              : p.periodicity_days 
                ? `${p.periodicity_days} dias`
                : "-";

          // Mapear OSs para formato padrão
          const mapOrder = (o: typeof formattedOrders[0]) => ({
            orderNumber: o.order_number ? `OS-${String(o.order_number).padStart(5, '0')}` : "-",
            type: o.description || o.plan_name || "Manutenção",
            providerName: o.provider_name || "Sem Fornecedor",
            openedAt: o.opened_at,
            closedAt: o.closed_at,
            cost: o.total_cost || 0,
            status: o.status,
            planId: o.plan_id,
          });

          // NOVA ESTRUTURA: Planos com OSs agrupadas
          const plansWithOrders = allVehiclePlans.map(p => {
            const planOrders = vehicleOrders.filter(o => o.plan_id === p.id);
            // Encontrar última execução baseada nas OSs concluídas deste plano
            const completedPlanOrders = planOrders.filter(o => o.status === 'closed');
            const lastExec = completedPlanOrders.length > 0 
              ? [...completedPlanOrders].sort((a, b) => (b.closed_at || "").localeCompare(a.closed_at || ""))[0]?.closed_at
              : null;
            
            return {
              planId: p.id,
              planName: p.name || "-",
              type: p.type || "-",
              interval: getInterval(p),
              lastExecution: lastExec,
              nextExecution: p.next_execution_date,
              status: getPlanStatus(p),
              workOrders: planOrders.map(mapOrder),
            };
          }).sort((a, b) => b.workOrders.length - a.workOrders.length); // Ordenar por qtd de OSs

          // OSs órfãs: sem plano vinculado OU de planos inativos (não relevantes)
          const orphanOrders = vehicleOrders
            .filter(o => !o.plan_id || !relevantPlanIds.has(o.plan_id))
            .map(mapOrder);

          // Legado: manter estrutura antiga para compatibilidade
          const plans = allVehiclePlans.map(p => ({
            planId: p.id,
            planName: p.name || "-",
            type: p.type || "-",
            interval: getInterval(p),
            lastExecution: null,
            nextExecution: p.next_execution_date,
            status: getPlanStatus(p),
          }));

          const workOrders = vehicleOrders.map(mapOrder);

          const pendingPlans = allVehiclePlans.filter(p => {
            const daysUntil = p.days_until_maintenance;
            const kmUntil = p.km_until_maintenance;
            return p.status === 'active' && ((daysUntil !== null && daysUntil <= 30) || (kmUntil !== null && kmUntil <= 2000));
          }).map(p => {
            let status: 'overdue' | 'critical' | 'upcoming' | 'scheduled' = 'scheduled';
            const daysUntil = p.days_until_maintenance;
            const kmUntil = p.km_until_maintenance;
            if ((daysUntil !== null && daysUntil < 0) || (kmUntil !== null && kmUntil < 0)) status = 'overdue';
            else if ((daysUntil !== null && daysUntil <= 7) || (kmUntil !== null && kmUntil < 500)) status = 'critical';
            else if ((daysUntil !== null && daysUntil <= 30) || (kmUntil !== null && kmUntil < 2000)) status = 'upcoming';
            return {
              vehicleLabel: p.vehicle_plate || "-",
              planName: p.name || "-",
              nextDate: p.next_execution_date,
              daysUntil,
              kmUntil,
              status,
            };
          });

          return {
            vehicle_id: vehicleId,
            vehicle_label: vehicle?.vehicle_label ?? "-",
            totalOrders: vehicleOrders.length,
            completedOrders,
            openOrders,
            totalCost,
            avgCostPerOrder: vehicleOrders.length > 0 ? totalCost / vehicleOrders.length : 0,
            lastMaintenanceDate: lastOrder?.closed_at || lastOrder?.opened_at || null,
            plansWithOrders,
            orphanOrders,
            plans,
            workOrders,
            pendingPlans,
          };
        };

        const input = buildMaintenancePdfInput({ 
          workOrders: formattedOrders, 
          pendingPlans: formattedPlans,
          pendingApprovalPlans, // NOVO: Planos pendentes de aprovação
          periodLabel, 
          companyName: activeCompany.name,
          vehiclesSummary,
          fetchVehicleMaintenanceDetail,
          options: {
            mode: currentFilters.consolidatedPdfMode ?? 'top-n',
            topN: currentFilters.consolidatedPdfTopN ?? 10,
            maxVehicles: currentFilters.consolidatedPdfMaxVehicles ?? 80,
          },
        });
        const blob = await generateMaintenancePDF_Pro(input);
        const filename = `FleetONE-Manutencao-${format(new Date(), "yyyy-MM-dd")}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        await auditRun.complete({ status: 'success', rowsProcessed: formattedOrders.length });
        toast({ title: "Sucesso", description: "PDF de Manutenção gerado", duration: 2000 });
      } else if (currentFilters.reportType === 'maintenance-detailed' && currentFilters.dateRange?.from && currentFilters.dateRange?.to) {
        // PDF Profissional para Manutenção Detalhado - Processar TODOS os veículos
        let vehicleIdsToProcess = [...currentFilters.vehicleIds];
        
        // Se nenhum veículo selecionado, expandir por centro de custo (frota)
        if (vehicleIdsToProcess.length === 0 && currentFilters.costCenterIds.length > 0) {
          const { data: fleetVehicles } = await supabase
            .from('fleet_vehicles')
            .select('id')
            .eq('company_id', activeCompany.id)
            .eq('status', 'active')
            .in('cost_center_id', currentFilters.costCenterIds);
          
          vehicleIdsToProcess = fleetVehicles?.map(v => v.id) || [];
        }
        
        // Se NENHUM filtro selecionado, buscar TODOS os veículos ativos
        if (vehicleIdsToProcess.length === 0 && currentFilters.costCenterIds.length === 0) {
          const { data: allVehicles } = await supabase
            .from('fleet_vehicles')
            .select('id')
            .eq('company_id', activeCompany.id)
            .eq('status', 'active');
          
          vehicleIdsToProcess = allVehicles?.map(v => v.id) || [];
        }
        
        // Validar que há veículos
        if (vehicleIdsToProcess.length === 0) {
          toast({ title: "Erro", description: "Nenhum veículo encontrado na empresa", variant: "destructive" });
          setIsGenerating(false);
          return;
        }
        
        // Buscar ordens de serviço de TODOS os veículos
        const { data: workOrdersData, error: woError } = await supabase
          .from('fleet_work_orders')
          .select(`
            id,
            order_number,
            description,
            status,
            total_cost,
            opened_at,
            closed_at,
            provider_id,
            vehicle_id,
            fleet_providers(id, name),
            fleet_vehicles(id, plate, make, model, odometer, status),
            fleet_work_order_items(id, description, quantity, unit_price, total_price)
          `)
          .eq('company_id', activeCompany.id)
          .in('vehicle_id', vehicleIdsToProcess)
          .in('status', ['closed', 'completed'])
          .gte('opened_at', currentFilters.dateRange.from.toISOString())
          .lte('opened_at', currentFilters.dateRange.to.toISOString())
          .order('opened_at', { ascending: false });
        
        if (woError) {
          throw new Error('Erro ao buscar ordens de serviço: ' + woError.message);
        }
        
        const workOrders = (workOrdersData || []) as unknown as WorkOrderWithItems[];
        
        // Função para calcular custo com fallback (total_cost -> soma de itens -> 0)
        const getWorkOrderCost = (wo: WorkOrderWithItems): number => {
          if (wo.total_cost && wo.total_cost > 0) return wo.total_cost;
          if (wo.fleet_work_order_items?.length > 0) {
            return wo.fleet_work_order_items.reduce((sum, item) => sum + (item.total_price || 0), 0);
          }
          return 0;
        };
        
        // Verificar se há dados financeiros nas OSs
        const hasFinancialData = workOrders.some(wo => 
          (wo.total_cost && wo.total_cost > 0) || 
          (wo.fleet_work_order_items?.some(i => i.total_price && i.total_price > 0))
        );
        
        if (!hasFinancialData && workOrders.length > 0) {
          toast({
            title: "Dados Incompletos",
            description: "As ordens de serviço não possuem valores financeiros cadastrados. O relatório mostrará apenas quantidades.",
            duration: 5000
          });
        }
        
        // Calcular estatísticas consolidadas (apenas OSs finalizadas - closed/completed)
        const totalOS = workOrders.length;
        const osFechadas = workOrders.length; // Todas são finalizadas (filtro aplicado na query)
        const osAbertas = 0; // Não incluídas no relatório
        const osEmAndamento = 0; // Não incluídas no relatório
        
        // Agregar por veículo
        const vehicleMap = new Map<string, { plate: string; model: string; osCount: number; itemCount: number; totalCost: number; odometer: number }>();
        for (const wo of workOrders) {
          const veh = wo.fleet_vehicles;
          if (!veh) continue;
          if (!vehicleMap.has(veh.id)) {
            vehicleMap.set(veh.id, { 
              plate: veh.plate, 
              model: `${veh.make || ''} ${veh.model || ''}`.trim() || 'N/A', 
              osCount: 0, 
              itemCount: 0, 
              totalCost: 0,
              odometer: veh.odometer || 0
            });
          }
          const summary = vehicleMap.get(veh.id)!;
          summary.osCount += 1;
          summary.itemCount += wo.fleet_work_order_items?.length || 1;
          summary.totalCost += getWorkOrderCost(wo);
        }
        const vehicleSummary = Array.from(vehicleMap.values()).sort((a, b) => b.totalCost - a.totalCost);
        
        // Agregar por fornecedor com detalhamento
        const providerMap = new Map<string, { providerName: string; workOrders: Array<{ vehiclePlate: string; orderNumber: number; openedAt: string; items: Array<{ qty: number; description: string; unitPrice: number; totalPrice: number }>; subtotal: number }>; total: number }>();
        for (const wo of workOrders) {
          const providerId = wo.provider_id || 'sem-fornecedor';
          const providerName = wo.fleet_providers?.name || 'Sem Fornecedor';
          
          if (!providerMap.has(providerId)) {
            providerMap.set(providerId, { providerName, workOrders: [], total: 0 });
          }
          
          const group = providerMap.get(providerId)!;
          const woCost = getWorkOrderCost(wo);
          
          // Parser para extrair itens da descrição (formato: "QTD DESCRICAO VALOR")
          const parseItemsFromDescription = (description: string): Array<{ qty: number; description: string; unitPrice: number; totalPrice: number }> => {
            if (!description) return [];
            const lines = description.split('\n').filter(l => l.trim());
            const items: Array<{ qty: number; description: string; unitPrice: number; totalPrice: number }> = [];
            
            for (const line of lines) {
              const trimmed = line.trim();
              
              // Padrão: "2 EMBUCHAMENTO EIXO DIANT.1,500" ou "2 GRAXA 120,00"
              const matchWithPrice = trimmed.match(/^(\d+)\s+(.+?)\s+([\d.,]+)$/);
              if (matchWithPrice) {
                const qty = parseInt(matchWithPrice[1], 10);
                const desc = matchWithPrice[2].trim();
                const priceStr = matchWithPrice[3].replace(/\./g, '').replace(',', '.');
                const price = parseFloat(priceStr) || 0;
                if (price > 0) {
                  items.push({ qty, description: desc, unitPrice: price, totalPrice: price * qty });
                  continue;
                }
              }
              
              // Padrão alternativo: "DESCRICAO 1.500,00"
              const matchDescPrice = trimmed.match(/^(.+?)\s+([\d.,]+)$/);
              if (matchDescPrice) {
                const desc = matchDescPrice[1].trim();
                const priceStr = matchDescPrice[2].replace(/\./g, '').replace(',', '.');
                const price = parseFloat(priceStr) || 0;
                if (price > 0) {
                  items.push({ qty: 1, description: desc, unitPrice: price, totalPrice: price });
                  continue;
                }
              }
            }
            return items;
          };
          
          let items: Array<{ qty: number; description: string; unitPrice: number; totalPrice: number }>;
          
          if (wo.fleet_work_order_items?.length) {
            items = wo.fleet_work_order_items.map(i => ({ 
              qty: i.quantity, 
              description: i.description, 
              unitPrice: i.unit_price || 0, 
              totalPrice: i.total_price || 0 
            }));
          } else if (wo.description) {
            const parsed = parseItemsFromDescription(wo.description);
            if (parsed.length > 0 && parsed.some(p => p.totalPrice > 0)) {
              items = parsed;
            } else {
              items = [{ qty: 1, description: wo.description, unitPrice: woCost, totalPrice: woCost }];
            }
          } else {
            items = [{ qty: 1, description: 'Servico', unitPrice: woCost, totalPrice: woCost }];
          }
          
          const subtotal = items.reduce((sum, i) => sum + (i.totalPrice || 0), 0);
          
          group.workOrders.push({
            vehiclePlate: wo.fleet_vehicles?.plate || 'N/A',
            orderNumber: wo.order_number,
            openedAt: wo.opened_at,
            items,
            subtotal
          });
          group.total += subtotal;
        }
        const providerGroups = Array.from(providerMap.values()).sort((a, b) => b.total - a.total);
        
        // Calcular KPIs
        const totalItens = providerGroups.reduce((sum, g) => sum + g.workOrders.reduce((s, wo) => s + wo.items.length, 0), 0);
        const totalCost = providerGroups.reduce((sum, g) => sum + g.total, 0);
        const totalOdometer = vehicleSummary.reduce((sum, v) => sum + v.odometer, 0);
        const topProvider = providerGroups[0] || { providerName: 'N/A', total: 0 };
        
        // Gerar PDF profissional
        const { generateDetailedMaintenancePDF_Pro: generateProPDF } = await import('@/hooks/use-report-export.maintenance-detailed.pro');
        
        const blob = await generateProPDF({
          companyName: activeCompany.name,
          periodLabel: `${format(currentFilters.dateRange.from, 'dd/MM/yyyy', { locale: ptBR })} a ${format(currentFilters.dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}`,
          generatedAtLabel: format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }),
          totalOS,
          totalItens,
          totalCost,
          osAbertas,
          osEmAndamento,
          osFechadas,
          vehicleCount: vehicleSummary.length,
          vehiclesInMaintenance: vehicleSummary.filter(v => v.osCount > 0).length,
          providerCount: providerGroups.length,
          topProvider: { name: topProvider.providerName, total: topProvider.total },
          costPerKm: totalOdometer > 0 ? totalCost / totalOdometer : 0,
          avgCostPerOS: totalOS > 0 ? totalCost / totalOS : 0,
          vehicleSummary,
          providerGroups,
          totalGeral: totalCost,
          hasFinancialData
        });
        
        // Download
        const filename = `FleetONE-ManutencaoDetalhado-${format(new Date(), "yyyy-MM-dd")}.pdf`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        await auditRun.complete({ status: 'success', rowsProcessed: totalOS });
        toast({ title: "Sucesso", description: "PDF de Manutenção Detalhado gerado", duration: 2000 });
      } else {
        // Fallback: PDF do HTML para outros tipos
        await new Promise(resolve => setTimeout(resolve, 500));
        const filename = `relatorio-${currentFilters.reportType}-${new Date().toISOString().split('T')[0]}.pdf`;
        const success = await generatePdfFromElement('report-preview-container', { filename, mode: 'paginated-a4', scale: 2, background: '#ffffff', marginMm: 10 });
        if (success) {
          await auditRun.complete({ status: 'success', rowsProcessed: (currentReportData?.byVehicle?.rows?.length || 0) });
          toast({ title: "Sucesso", description: "Relatório PDF gerado", duration: 2000 });
        } else {
          throw new Error("Falha ao gerar PDF");
        }
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      await auditRun.complete({ status: 'error', errorMessage: error instanceof Error ? error.message : 'Erro desconhecido' });
      toast({ title: "Erro", description: error instanceof Error ? error.message : "Falha ao gerar relatório", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  // Fallback para relatórios legados (vehicles, drivers, maintenance, etc)
  const fetchLegacyReportData = async (type: string, filters: UniversalReportFilters) => {
    if (!activeCompany?.id) return [];

    let query: any;
    
    switch (type) {
      case 'vehicles':
        query = supabase
          .from('fleet_vehicles')
          .select('*')
          .eq('company_id', activeCompany.id);
        break;
      case 'drivers':
        query = supabase
          .from('fleet_drivers')
          .select('*')
          .eq('company_id', activeCompany.id);
        break;
      case 'fuel':
        query = supabase
          .from('fleet_fuel_logs')
          .select('*, fleet_vehicles(plate, make, model), fleet_providers(name)')
          .eq('company_id', activeCompany.id)
          .order('occurred_at', { ascending: false });
        break;
      case 'documents':
        query = supabase
          .from('fleet_documents')
          .select('*, fleet_vehicles(plate)')
          .eq('company_id', activeCompany.id);
        break;
      case 'maintenance':
        // Buscar planos de manutenção
        const { data: plans, error: plansError } = await supabase
          .from('fleet_maint_plans')
          .select(`
            *,
            fleet_vehicles(id, plate, make, model, odometer)
          `)
          .eq('company_id', activeCompany.id);
        
        if (plansError) throw plansError;
        
        // Buscar ordens de serviço
        const { data: workOrders, error: woError } = await supabase
          .from('fleet_work_orders')
          .select(`
            *,
            fleet_vehicles(id, plate, make, model),
            fleet_providers(name)
          `)
          .eq('company_id', activeCompany.id);
        
        if (woError) throw woError;
        
        // Aplicar filtros de veículo se selecionados
        let filteredPlans = plans || [];
        let filteredWorkOrders = workOrders || [];
        
        if (filters.vehicleIds.length > 0) {
          filteredPlans = filteredPlans.filter(p => filters.vehicleIds.includes(p.vehicle_id));
          filteredWorkOrders = filteredWorkOrders.filter(wo => filters.vehicleIds.includes(wo.vehicle_id));
        }
        
        // Aplicar filtro de período nas OSs (se houver)
        if (filters.dateRange?.from && filters.dateRange?.to) {
          const fromDate = new Date(filters.dateRange.from);
          const toDate = new Date(filters.dateRange.to);
          filteredWorkOrders = filteredWorkOrders.filter(wo => {
            const woDate = new Date(wo.opened_at);
            return woDate >= fromDate && woDate <= toDate;
          });
        }
        
        // Retornar dados combinados
        return {
          plans: filteredPlans.map(plan => ({
            type: 'planned' as const,
            id: plan.id,
            name: plan.name,
            maintenance_type: plan.type,
            maintenance_category: plan.maintenance_category,
            status: plan.status,
            periodicity_km: plan.periodicity_km,
            periodicity_days: plan.periodicity_days,
            next_execution_km: plan.next_execution_km,
            next_execution_date: plan.next_execution_date,
            vehicle_id: plan.vehicle_id,
            plate: plan.fleet_vehicles?.plate,
            make: plan.fleet_vehicles?.make,
            model: plan.fleet_vehicles?.model,
            current_odometer: plan.fleet_vehicles?.odometer,
          })),
          workOrders: filteredWorkOrders.map(wo => ({
            type: 'realized' as const,
            id: wo.id,
            plan_id: wo.plan_id,
            vehicle_id: wo.vehicle_id,
            status: wo.status,
            total_cost: wo.total_cost || 0,
            description: wo.description,
            opened_at: wo.opened_at,
            closed_at: wo.closed_at,
            plate: wo.fleet_vehicles?.plate,
            make: wo.fleet_vehicles?.make,
            model: wo.fleet_vehicles?.model,
            provider_name: wo.fleet_providers?.name,
          })),
        };
      default:
        return [];
    }

    // Aplicar filtros de veículo
    if (filters.vehicleIds.length > 0) {
      query = query.in('vehicle_id', filters.vehicleIds);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  };

  const handleDownloadPDF = () => {
    if (!pdfBlob) return;
    
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-${appliedFilters.reportType}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Download iniciado",
      description: "O arquivo PDF foi baixado"
    });
  };

  const handlePrintPDF = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, '_blank');
  };

  // Cleanup ao desmontar componente
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const config = getReportConfig(appliedFilters.reportType);
  const needsPeriod = requiresPeriod(appliedFilters.reportType);

  const activeFiltersCount = [
    appliedFilters.dateRange?.from,
    appliedFilters.vehicleIds.length > 0,
    appliedFilters.driverIds.length > 0
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Relatórios
          </h1>
          
          <Select value={filters.reportType} onValueChange={handleReportTypeChange}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Selecione o tipo de relatório" />
            </SelectTrigger>
            <SelectContent>
              {availableReports.map(report => (
                <SelectItem key={report.id} value={report.id}>
                  {report.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Seletor de Período no Header */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-auto justify-start text-left font-normal",
                  !filters.dateRange?.from && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateRange?.from ? (
                  filters.dateRange.to ? (
                    `${format(filters.dateRange.from, "dd/MM/yy", { locale: ptBR })} - ${format(filters.dateRange.to, "dd/MM/yy", { locale: ptBR })}`
                  ) : format(filters.dateRange.from, "dd/MM/yy", { locale: ptBR })
                ) : "Período"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={filters.dateRange?.from}
                selected={{
                  from: filters.dateRange?.from,
                  to: filters.dateRange?.to
                }}
                onSelect={(range) => {
                  setFilters(prev => ({ ...prev, dateRange: { from: range?.from, to: range?.to } }));
                }}
                numberOfMonths={2}
                locale={ptBR}
                className="pointer-events-auto"
              />
              <div className="p-3 border-t flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getFullYear(), now.getMonth(), 1);
                    setFilters(prev => ({ ...prev, dateRange: { from: start, to: now } }));
                  }}
                >
                  Este mês
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const now = new Date();
                    const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                    setFilters(prev => ({ ...prev, dateRange: { from: start, to: now } }));
                  }}
                >
                  Últimos 90 dias
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2">
          {/* Sheet de Filtros - Apenas se não for Consolidado */}
          {filters.reportType !== 'consolidated' && (
            <UniversalFiltersSheet
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              requiresPeriod={needsPeriod}
              trigger={
                <Button variant="outline" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  Filtros
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              }
            />
          )}

          <Button 
            onClick={handleGeneratePDF} 
            disabled={
              !filters.reportType || 
              !filters.dateRange?.from || 
              !filters.dateRange?.to || 
              isGenerating || 
              isExporting
            }
          >
            {isGenerating ? 'Gerando...' : 'Gerar PDF'}
          </Button>

          {pdfUrl && (
            <>
              <Button variant="outline" onClick={handleDownloadPDF}>
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={handlePrintPDF}>
                <Printer className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div id="report-preview-container" className="flex-1 p-4 overflow-auto bg-white">
        {/* Relatório Consolidado */}
        {appliedFilters.reportType === 'consolidated' && reportData && period ? (
          <ConsolidatedReportWrapper
            summaryData={reportData}
            vehiclesSummary={vehiclesSummary}
            allVehiclesDetails={allVehiclesDetails}
            isLoadingDetails={isLoadingDetails}
            period={period}
            limitPerCategory={500}
          />
        ) : /* Relatório de Centro de Custo */
        appliedFilters.reportType === 'cost-centers' && reportData && period ? (
          <CostCenterReportWrapper
            costCenterGroups={costCenterGroups}
            allVehiclesDetails={allVehiclesDetails}
            isLoadingDetails={isLoadingDetails}
            period={period}
          />
        ) : /* Relatórios Especializados (Veículos, Manutenção, Combustível) */
        ['vehicles', 'maintenance', 'fuel'].includes(appliedFilters.reportType) && reportData && period ? (
          <SpecializedReportWrapper
            reportType={appliedFilters.reportType as 'vehicles' | 'maintenance' | 'fuel'}
            vehiclesSummary={vehiclesSummary}
            allVehiclesDetails={allVehiclesDetails}
            isLoadingDetails={isLoadingDetails}
            period={period}
          />
        ) : (
          /* Placeholder */
          <ReportContent
            reportType={appliedFilters.reportType}
            filters={appliedFilters}
            hasData={!!reportData}
            pdfUrl={pdfUrl}
            onDownload={handleDownloadPDF}
            onPrint={handlePrintPDF}
            isGenerating={isGenerating || isExporting}
            reportData={reportData}
          />
        )}
      </div>
    </div>
  );
}
